/**
 * Which Platform Console write controls a role may see.
 *
 * Each control mirrors the rule its own endpoint enforces, which is not always the permission the
 * role happens to hold: a channel partner admin is granted `platform.organizations.create` while
 * POST /platform/organizations admits `superadmin` and `enterprise_admin` by role, so gating New
 * Organization on that permission would show a button the server refuses.
 *
 * Fails closed: an action with no rule is refused, so a new control cannot ship visible-by-default.
 */

export type ConsoleAction =
  | 'createOrganization'
  | 'editOrganization'
  | 'deactivateOrganization'
  | 'reactivateOrganization'
  | 'permanentlyDeleteOrganization'
  | 'assignPartner'
  | 'manageSubscription'
  | 'viewOrganizationSubscription'
  | 'signComplianceAttestations'
  | 'viewPlanCatalog'
  | 'createPlan'
  | 'editPlan'
  | 'assignSubscriptionPlan'
  | 'viewOrganizationUsers'
  | 'importUsers'
  | 'inviteUser';

type ConsoleActionRule =
  | { superadminOnly: true }
  | { permission: string }
  // For an endpoint that takes a permission but refuses specific roles holding it. Neither of the
  // shapes above can express that, and approximating it with a permission alone shows a button the
  // server refuses.
  | { permission: string; deniedRoles: readonly string[] }
  // For an endpoint guarded by an explicit role list. Its permission would admit the wrong roles,
  // or no permission names the right ones.
  | { roles: readonly string[] };

export const CONSOLE_ACTION_RULES: Record<ConsoleAction, ConsoleActionRule> = {
  // Role-guarded on the server: channel_partner_admin holds platform.organizations.create and is
  // refused, and enterprise_admin holds no create permission at all.
  createOrganization: { roles: ['superadmin', 'enterprise_admin'] },
  editOrganization: { permission: 'platform.organizations.update' },
  // Deletion cascades to users; reassignment moves an organization between tenants.
  deactivateOrganization: { superadminOnly: true },
  reactivateOrganization: { superadminOnly: true },
  permanentlyDeleteOrganization: { superadminOnly: true },
  assignPartner: { superadminOnly: true },
  // Period reset, suspension, cancellation and change-request approval stay superadmin on the
  // server. Only assignment opened, and it has its own action below.
  manageSubscription: { superadminOnly: true },
  // Reading one organization's own subscription, which is what makes the assign grant usable. The
  // routes behind it carry a per-:orgId ownership guard.
  viewOrganizationSubscription: { permission: 'platform.subscriptions.organization_view' },
  // The HIPAA and BAA attestations. Deliberately NOT the same gate as the subscription content they
  // share a tab with: signing a BAA is platform-staff work, and platform.subscriptions.view is the
  // grant that carries it.
  signComplianceAttestations: { permission: 'platform.subscriptions.view' },
  // The plan catalog. Read and assign are wider than author: an enterprise may assign a
  // platform-global plan it may not edit.
  viewPlanCatalog: { permission: 'platform.plans.view' },
  createPlan: { permission: 'platform.plans.author' },
  editPlan: { permission: 'platform.plans.author' },
  assignSubscriptionPlan: { permission: 'platform.subscriptions.assign' },
  viewOrganizationUsers: { permission: 'platform.users.view' },
  // The import routes are role-gated rather than permission-gated and already name both partner
  // admin roles; this permission is the closest proxy that admits exactly those callers.
  importUsers: { permission: 'platform.users.manage' },
  // POST /platform/users/invite takes platform.users.manage and then refuses the partner admins,
  // which keep the permission for update, deactivate, reactivate, restore and password reset.
  inviteUser: {
    permission: 'platform.users.manage',
    deniedRoles: ['channel_partner_admin', 'partner_admin'],
  },
};

export function canUseConsoleAction(
  action: ConsoleAction,
  ctx: { role?: string; hasPermission: (permission: string) => boolean }
): boolean {
  if (!ctx.role) return false;

  const rule = CONSOLE_ACTION_RULES[action];
  if (!rule) return false;

  if ('superadminOnly' in rule) return ctx.role === 'superadmin';

  if ('roles' in rule) return rule.roles.includes(ctx.role);

  if ('deniedRoles' in rule && rule.deniedRoles.includes(ctx.role)) return false;

  return ctx.hasPermission(rule.permission);
}

const ENTERPRISE_ROLES = ['enterprise_admin', 'enterprise_user'];
const PARTNER_ROLES = [
  'partner_admin',
  'partner_user',
  'channel_partner_admin',
  'channel_partner_user',
];

/**
 * Whether the caller may act on an unaccepted invitation carrying `invitationRole`: send a
 * reminder (resend) or withdraw it (cancel). Both endpoints share one server guard.
 *
 * Mirrors `decideInvitationAccess` on the server, which is authoritative; this copy only decides
 * whether to offer the control. Tenancy is not checked here because the list endpoint already
 * returns only invitations inside the caller's scope.
 */
export function canManageInvitation(
  ctx: { role?: string; hasPermission: (permission: string) => boolean },
  invitationRole?: string
): boolean {
  if (!ctx.role) return false;
  if (ctx.role === 'superadmin') return true;
  if (!ctx.hasPermission('platform.users.manage')) return false;
  if (!invitationRole) return false;

  const forbidden = ENTERPRISE_ROLES.includes(ctx.role)
    ? ['superadmin']
    : PARTNER_ROLES.includes(ctx.role)
      ? ['superadmin', ...ENTERPRISE_ROLES]
      : ['superadmin', ...ENTERPRISE_ROLES, ...PARTNER_ROLES];

  return !forbidden.includes(invitationRole);
}

/**
 * Which endpoint renews an unaccepted invitation for this caller. Revive is superadmin-only; every
 * other console role sends an expired invitation again through resend, which sets it back to
 * pending with a fresh expiration.
 */
export function invitationRenewAction(isExpired: boolean, role?: string): 'revive' | 'resend' {
  return isExpired && role === 'superadmin' ? 'revive' : 'resend';
}

/** Resend (a reminder) and cancel are gated identically; kept as names for their call sites. */
export const canResendInvitation = canManageInvitation;
export const canCancelInvitation = canManageInvitation;

/**
 * Whether the caller may edit an invitation carrying `invitationRole`. PUT /platform/invitations/:id
 * admits superadmin and enterprise_admin by role, so the partner admins, which hold the same
 * permission, are not offered it. Whether the invitation was sent from inside the caller's
 * enterprise is the row's `canManage` flag, not decided here.
 */
export function canEditInvitation(
  ctx: { role?: string; hasPermission: (permission: string) => boolean },
  invitationRole?: string
): boolean {
  if (ctx.role !== 'superadmin' && ctx.role !== 'enterprise_admin') return false;
  return canManageInvitation(ctx, invitationRole);
}

export default {
  CONSOLE_ACTION_RULES,
  canUseConsoleAction,
  canManageInvitation,
  canResendInvitation,
  canCancelInvitation,
  canEditInvitation,
  invitationRenewAction,
};
